import { log } from 'client/utils/logging/logging';

export const formDataToJson = (formData = new FormData()) => {
    const enquiry = Object.fromEntries(formData);
    Object.keys(enquiry).forEach(key => {
        switch (key) {
            case 'geometry':
                enquiry[key] = JSON.parse(enquiry[key]);
                break;
            case 'draft':
            case 'isEmergency':
            case 'created':
                enquiry[key] = (enquiry[key] === 'true');
                break;
        }
    });
    return enquiry;
};

export const jsonToFormData = (json = {}) => {
    const formData = new FormData();
    Object.keys(json).forEach(key => {
        switch (key) {
            case 'geometry':
                formData.append(key, JSON.stringify(json[key]));
                break;
            case 'draft':
            case 'isEmergency':
            case 'created':
                formData.append(key, json[key] === true ? 'true' : 'false');
                break;
            default:
                formData.append(key, json[key]);
        }
    });
    return formData;
};

/**
 * Extracts the filename from a content-disposition header field
 * @param {string} contentDisposition With the format: attachment; filename*=UTF-8''<filename>
 * @param fallBackFileName The filename to use if a filename cannot be extracted from the field
 * @returns {string}
 */
export const extractEnquiryFileName = (contentDisposition, fallBackFileName) => {
    let fileName;
    try {
        fileName = decodeURIComponent(contentDisposition.split('filename*=UTF-8\'\'')[1]);
    } catch (URIError) {
        fileName = 'NUAR Map - ' + fallBackFileName + '.pdf';
    }
    if (!fileName || !fileName.endsWith('.pdf')) {
        const message = 'Unable to extract filename from: ' + contentDisposition;
        log('Enquiry', message, 'ERROR');
        throw new Error(message);
    }
    return fileName;
};

export function getSortedAoEnhancedMeasures (assetsInAOIOwners, featuresByAssetType) {
    if (!assetsInAOIOwners || !featuresByAssetType) {
        return [];
    }

    const priorityMapping = {
        'Permission required': 1,
        'Further consultation required': 2,
        'Special guidance required': 3,
        'Active monitoring of searches': 4,
        'Proceed with caution': 5
    };

    const ownerLookup = {};
    assetsInAOIOwners?.forEach(({ displayName, dataProviderId }) => {
        ownerLookup[dataProviderId] = displayName;
    });

    // Filter features that have enhanced measures & Map each feature to a new object with relevant properties
    const filterAndMapFeatures = (features, ownerLookup, priorityMapping) => {
        return features
            .filter(feature => feature.values_.enhancedmeasures)
            .map(feature => ({
                enhancedmeasures: feature.values_.enhancedmeasures,
                dataProviderId: feature.values_.dataproviderid_fk,
                displayName: ownerLookup[feature.values_.dataproviderid_fk],
                priority: priorityMapping[feature.values_.enhancedmeasures] || null
            }))
            .filter(feature => feature.displayName);
    };

    // Reduce the array to a Map to keep only the feature with the highest priority for each dataProviderId
    const highestPriorityFeaturePerDataOwner = (features) => {
        const featureMap = {};

        features.forEach((feature) => {
            const existingFeature = featureMap[feature.dataProviderId];
            if (!existingFeature || feature.priority < existingFeature.priority) {
                featureMap[feature.dataProviderId] = feature;
            }
        });

        return Object.values(featureMap);
    };

    const sortByPriorityThenName = (featureA, featureB) => {
        if (featureA.priority === featureB.priority) {
            return featureA.displayName.localeCompare(featureB.displayName);
        }
        return featureA.priority - featureB.priority;
    };

    const sortedAoEnhancedMeasures = highestPriorityFeaturePerDataOwner(
        Object.values(featuresByAssetType)
            .flatMap(features => filterAndMapFeatures(features, ownerLookup, priorityMapping))
    ).sort(sortByPriorityThenName);

    return sortedAoEnhancedMeasures;
}
